<!-- Fullscreen Document Viewer -->
@if (viewingDocumentUrl()) {
  <div class="fixed inset-0 bg-black/80 z-50 flex items-center justify-center animate-fade-in" (click)="closeDocumentViewer()">
    <div class="relative p-4">
      <button (click)="closeDocumentViewer()" class="absolute -top-4 -right-4 h-10 w-10 bg-white/20 rounded-full text-white text-2xl flex items-center justify-center hover:bg-white/40">&times;</button>
      <img [src]="viewingDocumentUrl()" class="max-w-[90vw] max-h-[90vh] object-contain" alt="Document Viewer">
    </div>
  </div>
}

<!-- Confirmation Modal -->
@if (confirmationModal(); as modal) {
  <div class="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4 animate-fade-in">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col" style="max-height: 90vh;">
      <header class="p-6 border-b">
        <h2 class="text-xl font-semibold text-[var(--cordic-black)]">{{ modal.title }}</h2>
      </header>
      <main #modalContent class="flex-1 overflow-y-auto p-6" (scroll)="onConfirmationScroll($event)">
        <div class="text-left leading-relaxed" [innerHTML]="modal.content"></div>
      </main>
      <footer class="p-4 bg-[var(--extra-light-tint)] border-t flex justify-end gap-3">
        <button type="button" (click)="confirmationModal.set(null)" class="btn btn-secondary">Cancel</button>
        <button type="button" (click)="acceptConfirmation()" [disabled]="!modal.canAgree" class="btn btn-primary">I Agree</button>
      </footer>
    </div>
  </div>
}


<div class="min-h-screen py-8 sm:py-12 px-4">
  <div class="max-w-4xl mx-auto">
    
    <!-- Header -->
    <header class="text-center mb-8 sm:mb-16 relative">
      <img [src]="logoUrl" alt="SuperYes Logo" class="mx-auto h-16 sm:h-20" />
      <p class="mt-4 text-base sm:text-lg text-[var(--cordic-black)]">
        Join SuperYes and stop leaving money on the table
      </p>
      @if (supabaseService.session() && currentStep() > 1 && currentStep() < steps.length) {
        <div class="mt-3 sm:mt-0 sm:absolute sm:top-0 sm:right-0">
          <button type="button" (click)="startOver()" class="text-sm text-[var(--cordic-black-80)] hover:text-[var(--cordic-black)] underline">
            Use a different account
          </button>
        </div>
      }
    </header>

    <!-- Progress Bar -->
    @if (currentStep() > 1 && currentStep() < steps.length) {
      <div class="mb-8 sm:mb-12 max-w-lg mx-auto">
         <app-progress-bar [steps]="progressBarSteps" [currentStep]="currentStep() - 1" />
      </div>
    }

    <!-- Main Content Card -->
    <main class="main-card">
      <!-- Rejection Banner -->
      @if (data().status === 'rejected' && data().rejectionReason) {
        <div class="rounded-lg bg-[var(--action-alert-light)] p-4 mb-8 border border-[var(--action-alert)]/30 animate-fade-in">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-[var(--action-alert-dark)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-md font-semibold text-[var(--action-alert-dark)]">Your application requires changes</h3>
              <div class="mt-2 text-sm text-[var(--action-alert-dark)] opacity-90 space-y-2">
                <p class="font-medium">Reason from our review team:</p>
                <p class="pl-2 border-l-2 border-[var(--action-alert-dark)]/40">{{ data().rejectionReason }}</p>
                <p class="mt-2">Please review your information, make any necessary updates, and resubmit your application from the final step.</p>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Initialization Error Display -->
      @if (initError()) {
        <div class="rounded-md bg-[var(--action-alert-light)] p-4 mb-6 animate-fade-in">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-[var(--action-alert)]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-[var(--action-alert-dark)]">Application Error</h3>
              <div class="mt-2 text-sm text-[var(--action-alert-dark)]">
                <p>{{ initError() }}</p>
              </div>
            </div>
          </div>
        </div>
      }

      @switch (currentStep()) {

        <!-- Step 1: Email Verification -->
        @case (1) {
          <div class="animate-fade-in">
            @if (isOtpRequested()) {
              <div class="text-center">
                 <svg class="mx-auto h-12 w-12 text-[var(--action-success-dark)]" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                <h2 class="mt-4 text-2xl font-semibold text-[var(--cordic-black)]">Check your inbox</h2>
                <p class="mt-2 text-[var(--cordic-black-80)] whitespace-pre-line max-w-md mx-auto">We’ve sent an email to <strong>{{ data().email }}</strong>. Enter the 6-digit verification code to secure your account.</p>
                
                <!-- Token Input Form -->
                <div class="mt-8 max-w-sm mx-auto">
                  <label for="token" class="block text-sm font-medium leading-6 text-[var(--cordic-black)] sr-only">Verification code</label>
                  <div class="mt-2">
                    <input type="text" name="token" id="token" #tokenInputRef
                          [value]="token()" (input)="updateToken($event)" 
                          [disabled]="authStatus() !== 'idle'"
                          class="form-input block w-full text-center text-2xl tracking-[0.5em] font-mono" 
                          placeholder="------"
                          maxlength="6" inputmode="numeric" pattern="[0-9]*">
                  </div>
                  @if (authError()) {
                    <p class="mt-2 text-sm text-left text-[var(--action-alert-dark)]">{{ authError() }}</p>
                  }
                  <div class="mt-6">
                    <button (click)="verifyToken()" [disabled]="!isTokenValid() || authStatus() !== 'idle'" type="button" 
                            class="btn btn-primary w-full transition-colors"
                            [class.bg-[var(--action-success-dark)]]="authStatus() === 'success'"
                            [class.cursor-default]="authStatus() === 'success'">
                      @switch (authStatus()) {
                        @case ('submitting') {
                          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          <span>Verifying...</span>
                        }
                        @case ('success') {
                          <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.707a1 1 0 00-1.414-1.414L9 10.586 7.557 9.143a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                          <span>Success!</span>
                        }
                        @default {
                          <span>Verify Code</span>
                        }
                      }
                    </button>
                  </div>

                  <div class="mt-6 text-sm text-center text-[var(--cordic-black-80)]">
                    <p class="mb-2">
                        <a (click)="isOtpRequested.set(false); authStatus.set('idle');" class="underline cursor-pointer hover:text-[var(--cordic-primary-100)]">Entered your email incorrectly?</a>
                    </p>
                    <p>
                        Didn’t receive it? Check spam or
                        <button (click)="resendOtp()" [disabled]="resendCooldown() > 0" class="underline hover:text-[var(--cordic-primary-100)] disabled:text-[var(--cordic-black-40)] disabled:no-underline disabled:cursor-not-allowed">
                          resend the code @if (resendCooldown() > 0) { ({{ resendCooldown() }}s) }
                        </button>
                    </p>
                  </div>
                </div>
              </div>
            } @else {
              <h2 class="text-2xl font-semibold text-[var(--cordic-black)]">Create your SuperYes account</h2>
              <p class="mt-2 text-[var(--cordic-black-80)]">Enter your email to get started. We’ll use it to set up and manage your account.</p>
              <div class="mt-8">
                <label for="email" class="form-label">Email</label>
                <div class="mt-2">
                  <input type="email" name="email" id="email" [value]="data().email" (input)="updateEmail($event)" (keydown.enter)="requestOtp()" class="form-input block w-full" placeholder="you@example.com">
                </div>
              </div>

              @if (authError()) {
                <p class="mt-2 text-sm text-[var(--action-alert-dark)]">{{ authError() }}</p>
              }

              <div class="mt-8">
                <button (click)="requestOtp()" [disabled]="!isStep1Valid() || authStatus() === 'submitting'" type="button" class="btn btn-primary w-full">
                   @if (authStatus() === 'submitting') {
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    <span>Sending...</span>
                   } @else {
                    <span>Send Access Code</span>
                   }
                </button>
              </div>
            }
          </div>
        }

        <!-- Step 2: Fleet Details -->
        @case (2) {
          <div class="animate-fade-in" (keydown.enter)="handleStep2SubmitOnEnter($event)">
            <h2 class="text-2xl font-semibold text-[var(--cordic-black)]">Fleet contact details</h2>
            <p class="mt-2 text-[var(--cordic-black-80)]">Tell us about your business and the best person to contact.</p>
            
            <div class="mt-8 space-y-8">
              <div class="space-y-8">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8 sm:items-start">
                  <div>
                    <label for="legalEntityName" class="form-label">Legal Entity Name</label>
                    <input type="text" id="legalEntityName" [value]="data().contactDetails.legalEntityName" (input)="updateContactDetails('legalEntityName', $event)" 
                      class="form-input mt-2 block w-full"
                      [class.border-red-500]="!isLegalEntityNameValid() && data().contactDetails.legalEntityName.length > 0">
                    @if (!isLegalEntityNameValid() && data().contactDetails.legalEntityName.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Must be at least 3 characters long.</p>
                    }
                  </div>
                   <div>
                    <label class="form-label hidden sm:block">&nbsp;</label>
                    <div class="sm:mt-2 flex items-center justify-between p-3">
                      <label for="useLegalNameToggle" class="font-medium text-gray-900 pr-4 cursor-pointer">
                        Use as fleet name
                      </label>
                      <button type="button" 
                              id="useLegalNameToggle"
                              (click)="toggleUseLegalNameAsFleetName()"
                              [attr.aria-checked]="data().contactDetails.useLegalNameAsFleetName"
                              role="switch"
                              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--superyes-green)] focus:ring-offset-2"
                              [class.bg-[var(--superyes-green)]]="data().contactDetails.useLegalNameAsFleetName"
                              [class.bg-gray-200]="!data().contactDetails.useLegalNameAsFleetName">
                        <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                              [class.translate-x-5]="data().contactDetails.useLegalNameAsFleetName"
                              [class.translate-x-0]="!data().contactDetails.useLegalNameAsFleetName">
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                  <div>
                    <label for="fleetName" class="form-label">Fleet Name / Trading As</label>
                    <input type="text" id="fleetName" [value]="data().contactDetails.fleetName" (input)="updateContactDetails('fleetName', $event)" 
                      [disabled]="data().contactDetails.useLegalNameAsFleetName"
                      class="form-input mt-2 block w-full disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                      [class.border-red-500]="!isFleetNameValid() && data().contactDetails.fleetName.length > 0">
                    @if (!isFleetNameValid() && data().contactDetails.fleetName.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Must be at least 3 characters long.</p>
                    }
                  </div>
                  <div>
                    <label for="fleetPhone" class="form-label">Booking Phone Number</label>
                    <input type="tel" id="fleetPhone" [value]="data().contactDetails.fleetPhone" (input)="updateContactDetails('fleetPhone', $event)"
                      inputmode="numeric" pattern="[0-9]*"
                      class="form-input mt-2 block w-full"
                      [class.border-red-500]="!isFleetPhoneValid() && data().contactDetails.fleetPhone.length > 0">
                    @if (!isFleetPhoneValid() && data().contactDetails.fleetPhone.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Must be at least 7 characters long.</p>
                    }
                  </div>
                </div>

                <div>
                  <label for="address" class="form-label">Registered Address</label>
                  <div class="relative mt-2">
                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.1.4-.223.654-.369.889-.496 1.976-1.229 2.948-2.135C15.423 15.34 16 14.182 16 13a6 6 0 00-12 0c0 1.182.577 2.34 1.732 3.324.972.906 2.059 1.639 2.948 2.135.254.146.468.269.654-.369a5.741 5.741 0 00.281.14l.018.008.006.003zM10 8a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" /></svg>
                    </div>
                    <input type="text" #addressInput id="address" 
                      [value]="addressInputText()"
                      (input)="addressInputText.set(addressInput.value)"
                      class="form-input mt-2 block w-full pl-10"
                      [class.border-red-500]="!isAddressValid() && addressInputText().length > 0">
                  </div>
                  @if (!isAddressValid() && addressInputText().length > 0) {
                    <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Please select a valid address from the suggestions.</p>
                  } @else {
                    <p class="mt-2 text-sm text-[var(--cordic-black-80)]">Start typing your address for auto-complete suggestions.</p>
                  }
                </div>
              </div>

              <div class="border-t border-[var(--cordic-black-10)]"></div>

              <!-- Primary Contact Section -->
              <div class="space-y-8">
                <div>
                  <h3 class="text-lg font-medium leading-6 text-[var(--cordic-black)]">Primary Contact</h3>
                  <p class="mt-1 text-sm text-[var(--cordic-black-80)]">This person will be our main point of contact regarding this application.</p>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-8">
                  <div>
                    <label for="contactName" class="form-label">Contact Name</label>
                    <input type="text" id="contactName" [value]="data().contactDetails.contactName" (input)="updateContactDetails('contactName', $event)" 
                      class="form-input mt-2 block w-full"
                      [class.border-red-500]="!isContactNameValid() && data().contactDetails.contactName.length > 0">
                    @if (!isContactNameValid() && data().contactDetails.contactName.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Must be at least 3 characters long.</p>
                    }
                  </div>
                  <div>
                    <label for="contactEmail" class="form-label">Email</label>
                    <input type="email" id="contactEmail" [value]="data().contactDetails.contactEmail" (input)="updateContactDetails('contactEmail', $event)"
                      class="form-input mt-2 block w-full"
                      [class.border-red-500]="!isContactEmailValid() && data().contactDetails.contactEmail.length > 0">
                    @if (!isContactEmailValid() && data().contactDetails.contactEmail.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Please enter a valid email address.</p>
                    }
                  </div>
                  <div>
                    <label for="contactPhone" class="form-label">Phone</label>
                    <input type="tel" id="contactPhone" [value]="data().contactDetails.contactPhone" (input)="updateContactDetails('contactPhone', $event)"
                      inputmode="numeric" pattern="[0-9]*"
                      class="form-input mt-2 block w-full"
                      [class.border-red-500]="!isContactPhoneValid() && data().contactDetails.contactPhone.length > 0">
                    @if (!isContactPhoneValid() && data().contactDetails.contactPhone.length > 0) {
                      <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Must be at least 7 characters long.</p>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }


        <!-- Step 3: Fleet Operations -->
        @case (3) {
          <div class="animate-fade-in">
            <h2 class="text-2xl font-semibold text-[var(--cordic-black)]">About your fleet</h2>
            <p class="mt-2 text-[var(--cordic-black-80)]">Help us understand how your fleet operates.</p>
            <div class="mt-8 space-y-10">
              <div class="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div>
                  <label for="fleetSize" class="form-label">Fleet Size</label>
                  <select id="fleetSize" [value]="data().fleetOps.fleetSize" (change)="updateFleetOps('fleetSize', $event)" class="form-select mt-2 block w-full">
                    <option value="" disabled>Select a range</option>
                    <option value="<20">&lt;20 vehicles</option>
                    <option value="20-40">20 - 40 vehicles</option>
                    <option value="40-60">40 - 60 vehicles</option>
                    <option value="60-100">60 - 100 vehicles</option>
                    <option value="100-150">100 - 150 vehicles</option>
                    <option value="150-300">150 - 300 vehicles</option>
                    <option value="300+">300+ vehicles</option>
                  </select>
                </div>
                <div>
                  <label for="serviceLevel" class="form-label">Service Level</label>
                  <select id="serviceLevel" [value]="data().fleetOps.serviceLevel" (change)="updateFleetOps('serviceLevel', $event)" class="form-select mt-2 block w-full">
                    <option value="" disabled>Select a level</option>
                    <option value="Platinum">Platinum (Chauffeur)</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Bronze">Bronze (Minicab)</option>
                  </select>
                </div>
              </div>
              
              <div>
                <h3 class="text-md font-medium text-center text-[var(--cordic-black)]">Booking mix</h3>
                <p class="mt-1 text-sm text-center text-[var(--cordic-black-80)]">Drag the dot to adjust the percentage</p>
                <div class="mt-4">
                  <input type="range" id="bookingSplit" min="0" max="100" step="5" 
                    [value]="data().fleetOps.workSplit.bookingType.prebook" 
                    (input)="updateBookingSplit($event)" 
                    class="w-full h-2 rounded-lg appearance-none cursor-pointer custom-slider custom-slider-bicolor"
                    [style.--bicolor-stop]="data().fleetOps.workSplit.bookingType.prebook + '%'">
                </div>
                <div class="flex justify-between gap-4 mt-4">
                  <div class="flex-1 text-center p-3 border rounded-lg border-[var(--cordic-black-40)] bg-white">
                    <div class="text-lg font-bold text-[var(--cordic-black)]">{{ data().fleetOps.workSplit.bookingType.prebook }}%</div>
                    <div class="text-sm text-[var(--cordic-black-80)]">Pre-book</div>
                  </div>
                  <div class="flex-1 text-center p-3 border rounded-lg border-[var(--cordic-black-40)] bg-white">
                    <div class="text-lg font-bold text-[var(--cordic-black)]">{{ data().fleetOps.workSplit.bookingType.asap }}%</div>
                    <div class="text-sm text-[var(--cordic-black-80)]">ASAP</div>
                  </div>
                </div>
              </div>

              <div>
                 @let paymentSplit = data().fleetOps.workSplit.paymentType;
                 <h3 class="text-md font-medium text-center text-[var(--cordic-black)]">Payment split</h3>
                 <p class="mt-1 text-sm text-center text-[var(--cordic-black-80)]">Drag the 2 dots to adjust the percentage</p>
                 <div class="mt-8 mb-4">
                    <div #sliderTrack class="relative w-full h-2 rounded-full bg-[var(--cordic-black-10)]">
                        <div class="absolute h-full rounded-l-full bg-[var(--cordic-secondary-100)]" [style.width.%]="paymentSplit.account"></div>
                        <div class="absolute h-full bg-[var(--cordic-primary-100)]" [style.left.%]="paymentSplit.account" [style.width.%]="paymentSplit.card"></div>
                        <div class="absolute h-full rounded-r-full bg-[var(--cordic-black-40)]" [style.left.%]="paymentSplit.account + paymentSplit.card" [style.width.%]="paymentSplit.cash"></div>

                        <button (mousedown)="startDrag($event, 'account')" (touchstart)="startDrag($event, 'account')"
                            class="absolute top-1/2 -translate-x-1/2 -translate-y-1.5 w-6 h-6 rounded-full bg-[var(--cordic-primary-100)] shadow-lg border-2 border-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            [class.cursor-grabbing]="activeHandle() === 'account'" [class.cursor-grab]="activeHandle() !== 'account'"
                            [style.left.%]="paymentSplit.account">
                        </button>
                        <button (mousedown)="startDrag($event, 'card')" (touchstart)="startDrag($event, 'card')"
                            class="absolute top-1/2 -translate-x-1/2 -translate-y-1.5 w-6 h-6 rounded-full bg-[var(--cordic-primary-100)] shadow-lg border-2 border-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            [class.cursor-grabbing]="activeHandle() === 'card'" [class.cursor-grab]="activeHandle() !== 'card'"
                            [style.left.%]="paymentSplit.account + paymentSplit.card">
                        </button>
                    </div>
                 </div>
                 <div class="flex justify-between gap-4 mt-4">
                    <div class="flex-1 text-center p-3 border rounded-lg border-[var(--cordic-black-40)] bg-white">
                        <div class="text-lg font-bold text-[var(--cordic-black)]">{{ paymentSplit.account }}%</div>
                        <div class="text-sm text-[var(--cordic-black-80)]">Account</div>
                    </div>
                    <div class="flex-1 text-center p-3 border rounded-lg border-[var(--cordic-black-40)] bg-white">
                        <div class="text-lg font-bold text-[var(--cordic-black)]">{{ paymentSplit.card }}%</div>
                        <div class="text-sm text-[var(--cordic-black-80)]">Card</div>
                    </div>
                    <div class="flex-1 text-center p-3 border rounded-lg border-[var(--cordic-black-40)] bg-white">
                        <div class="text-lg font-bold text-[var(--cordic-black)]">{{ paymentSplit.cash }}%</div>
                        <div class="text-sm text-[var(--cordic-black-80)]">Cash</div>
                    </div>
                 </div>
              </div>

              <div>
                <div class="sm:flex sm:items-start sm:gap-4">
                  <div class="flex-1 min-w-0">
                    <label for="dispatchSystem" class="form-label">Dispatch System</label>
                    <select id="dispatchSystem" [value]="data().fleetOps.dispatchSystem" (change)="updateFleetOps('dispatchSystem', $event)" class="form-select mt-2 block w-full">
                      <option value="" disabled>Select a system</option>
                      <option>AutoCab</option> <option>Cab9</option> <option>CabTreasure</option> <option>Cordic</option> <option>iCabbi</option> <option>Magenta</option> <option>Sherlock</option> <option>Other</option>
                    </select>
                  </div>

                  @if (data().fleetOps.dispatchSystem === 'Other') {
                    <div class="flex-1 min-w-0 mt-4 sm:mt-0 animate-fade-in">
                      <label for="dispatchSystemOther" class="form-label">Please specify</label>
                      <input type="text" id="dispatchSystemOther" [value]="data().fleetOps.dispatchSystemOther" (input)="updateFleetOps('dispatchSystemOther', $event)"
                        class="form-input mt-2 block w-full"
                        placeholder="e.g., FleetMaster">
                    </div>
                  }
                </div>
                @if (data().fleetOps.dispatchSystem === 'Other' && data().fleetOps.dispatchSystemOther.trim().length <= 2) {
                  <p class="mt-2 text-sm text-[var(--action-alert-dark)]">Please enter the name of your dispatch system.</p>
                }
              </div>
            </div>
          </div>
        }

        <!-- Step 4: Documents -->
        @case (4) {
          <div class="animate-fade-in">
            <h2 class="text-2xl font-semibold text-[var(--cordic-black)]">Upload documents</h2>
            <p class="mt-2 text-[var(--cordic-black-80)]">Upload your Operator’s Licence and Public & Employer’s Liability insurance.</p>

            @if (uploadError()) {
              <div class="rounded-md bg-[var(--action-alert-light)] p-4 my-6 animate-fade-in">
                <div class="flex">
                  <div class="flex-shrink-0"> <svg class="h-5 w-5 text-[var(--action-alert)]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" /></svg></div>
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-[var(--action-alert-dark)]">Upload Failed</h3>
                    <div class="mt-2 text-sm text-[var(--action-alert-dark)]"><p>{{ uploadError() }} Please try again.</p></div>
                  </div>
                </div>
              </div>
            }

            <!-- DESKTOP VIEW -->
            <div class="mt-8 hidden md:grid md:grid-cols-1 lg:grid-cols-3 gap-6">
              @for (docKey of documentKeys; track docKey) {
                @let docTitle = documentTitles[docKey];
                @let document = data().documents[docKey];
                <div class="bg-slate-50/70 rounded-xl p-4 flex flex-col border">
                  <ng-container *ngTemplateOutlet="documentUploader; context: { document: document, docKey: docKey, docTitle: docTitle }"></ng-container>
                </div>
              }
            </div>
            
            <!-- MOBILE VIEW -->
            <div class="mt-6 md:hidden space-y-4">
              @for (docKey of documentKeys; track docKey) {
                @let doc = data().documents[docKey];
                @let docTitle = documentTitles[docKey];
                <div class="rounded-lg border p-4">
                  <div class="flex items-center justify-between gap-4">
                    <h3 class="font-medium text-gray-900 flex-1 truncate">{{ docTitle }}</h3>
                    
                    @if (isUploadingDoc() === docKey) {
                      <div class="flex items-center justify-center text-sm text-gray-500 flex-shrink-0">
                        <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Uploading...</span>
                      </div>
                    } @else if (doc?.url) {
                      <div class="flex items-center gap-3 flex-shrink-0">
                        <svg class="h-6 w-6 text-[var(--action-success-dark)]" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <button type="button" (click)="viewDocument(doc)" class="text-sm font-medium text-[var(--superyes-green)] hover:underline">View</button>
                        <label [for]="docKey + '-mobile-input'" class="text-sm font-medium text-gray-500 hover:underline cursor-pointer">Replace
                          <input [id]="docKey + '-mobile-input'" type="file" class="sr-only" accept="image/*,application/pdf" (change)="handleFileInput($event, docKey)" capture="environment">
                        </label>
                      </div>
                    } @else {
                      <label [for]="docKey + '-mobile-input'" class="btn btn-secondary !py-1 !px-3 text-sm cursor-pointer flex-shrink-0">
                        Upload File
                        <input [id]="docKey + '-mobile-input'" type="file" class="sr-only" accept="image/*,application/pdf" (change)="handleFileInput($event, docKey)" capture="environment">
                      </label>
                    }
                  </div>
                  @if (docValidationErrors()[docKey] && !doc?.url) {
                    <p class="mt-2 text-sm text-[var(--action-alert-dark)]">{{ docValidationErrors()[docKey] }}</p>
                  }
                </div>
              }
            </div>

          </div>
        }

        <!-- Step 5: Confirmations -->
        @case (5) {
          <div class="animate-fade-in">
            <h2 class="text-2xl font-semibold text-[var(--cordic-black)]">Review and agree</h2>
            <p class="mt-2 text-[var(--cordic-black-80)]">These agreements ensure reliable job sharing, trusted behaviour, and data protection across the SuperYes network.</p>
            @let confirmations = data().confirmations;
            <div class="mt-8 space-y-4">
              <!-- Terms of Service -->
              <div (click)="openConfirmationModal('terms')" class="rounded-lg border p-4 flex items-center justify-between cursor-pointer transition-all hover:border-[var(--superyes-green)] hover:bg-slate-50 min-h-20" [class.border-green-500]="confirmations.terms" [class.bg-slate-50]="confirmations.terms">
                <div class="flex items-center w-1/2 sm:w-auto">
                  @if (confirmations.terms) { <svg class="h-6 w-6 text-[var(--action-success-dark)] mr-3" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
                  <span class="font-medium text-[var(--cordic-black)]">Terms of Service</span>
                </div>
                <span class="flex-shrink-0 text-right text-sm font-semibold text-[var(--superyes-green)]">{{ confirmations.terms ? 'View Again' : 'Read and Agree' }}</span>
              </div>
              <!-- Data Sharing Agreement -->
              <div (click)="openConfirmationModal('dataSharing')" class="rounded-lg border p-4 flex items-center justify-between cursor-pointer transition-all hover:border-[var(--superyes-green)] hover:bg-slate-50 min-h-20" [class.border-green-500]="confirmations.dataSharing" [class.bg-slate-50]="confirmations.dataSharing">
                <div class="flex items-center w-1/2 sm:w-auto">
                  @if (confirmations.dataSharing) { <svg class="h-6 w-6 text-[var(--action-success-dark)] mr-3" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
                  <span class="font-medium text-[var(--cordic-black)]">Data Sharing Agreement</span>
                </div>
                <span class="flex-shrink-0 text-right text-sm font-semibold text-[var(--superyes-green)]">{{ confirmations.dataSharing ? 'View Again' : 'Read and Agree' }}</span>
              </div>
              <!-- Code of Conduct -->
              <div (click)="openConfirmationModal('codeOfConduct')" class="rounded-lg border p-4 flex items-center justify-between cursor-pointer transition-all hover:border-[var(--superyes-green)] hover:bg-slate-50 min-h-20" [class.border-green-500]="confirmations.codeOfConduct" [class.bg-slate-50]="confirmations.codeOfConduct">
                <div class="flex items-center w-1/2 sm:w-auto">
                  @if (confirmations.codeOfConduct) { <svg class="h-6 w-6 text-[var(--action-success-dark)] mr-3" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
                  <span class="font-medium text-[var(--cordic-black)]">Code of Conduct</span>
                </div>
                <span class="flex-shrink-0 text-right text-sm font-semibold text-[var(--superyes-green)]">{{ confirmations.codeOfConduct ? 'View Again' : 'Read and Agree' }}</span>
              </div>
            </div>

            <!-- Additional Notes -->
            <div class="mt-8">
              <label for="additionalNotes" class="form-label">
                Additional Notes <span class="font-normal text-[var(--cordic-black-60)]">(optional)</span>
              </label>
              <textarea
                id="additionalNotes"
                rows="4"
                maxlength="1000"
                [value]="data().additionalNotes"
                (input)="updateAdditionalNotes($event)"
                class="form-input mt-2 block w-full"
                placeholder="Anything else you'd like us to know about your fleet or application?"></textarea>
              <p class="mt-2 text-sm text-[var(--cordic-black-60)] text-right">{{ data().additionalNotes.length }}/1000</p>
            </div>
          </div>
        }

        <!-- Step 6: Completion -->
        @case (6) {
            <div class="animate-fade-in text-center py-8">
                <svg class="mx-auto h-16 w-16 text-[var(--superyes-green)]" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>

                @if (wasRejected()) {
                  <h2 class="mt-4 text-3xl font-bold tracking-tight text-[var(--cordic-black)]">Application Resubmitted</h2>
                  <div class="mt-4 space-y-2 text-lg text-[var(--cordic-black-80)] max-w-2xl mx-auto">
                      <p>Thank you. We have received your updated application.</p>
                      <p>Our team will review your changes, and we’ll be in touch within <strong>2-3 business days</strong>.</p>
                  </div>
                } @else {
                  <h2 class="mt-4 text-3xl font-bold tracking-tight text-[var(--cordic-black)]">Welcome to SuperYes</h2>
                  <div class="mt-4 space-y-2 text-lg text-[var(--cordic-black-80)] max-w-2xl mx-auto">
                      <p>We’re reviewing your details and documents now.</p>
                      <p>Once approved, you’ll get access to configure your setup and start sharing and receiving jobs across the network.</p>
                      <p>We’ll be in touch within <strong>2-3 business days</strong> – you’re almost there!</p>
                  </div>
                }

                <div class="mt-12 pt-8 border-t border-[var(--cordic-black-10)]">
                    <h3 class="text-xl font-semibold text-[var(--cordic-black)]">Get even more value from SuperYes</h3>
                    <p class="mt-2 max-w-xl mx-auto text-[var(--cordic-black-80)]">SuperYes works best when more trusted fleets are connected. Invite nearby fleets to unlock more jobs and more reliable service.</p>
                    <a href="mailto:?subject=Join me on SuperYes!&body=I'm using SuperYes to share and receive jobs from other trusted fleets, and I thought you'd be interested. It helps us fill dead mileage and provide better service. You can sign up here: [Your App's URL]" 
                       class="mt-6 btn btn-success">
                       Invite a Fleet <span aria-hidden="true" class="ml-2">→</span>
                    </a>
                </div>
            </div>
        }
      }

      <!-- Navigation -->
      @if (currentStep() > 1 && currentStep() < 5) {
        <div class="mt-10 pt-8 border-t border-[var(--cordic-black-10)] flex items-center justify-between">
          <button (click)="saveAndGo('prev')" type="button" class="btn btn-secondary">Previous</button>
          <button (click)="saveAndGo('next')" [disabled]="(currentStep() === 2 && !isStep2Valid()) || (currentStep() === 3 && !isStep3Valid()) || (currentStep() === 4 && !isStep4Valid())" type="button" class="btn btn-primary">
            <span class="hidden sm:inline">Save and Continue</span>
            <span class="sm:hidden">Continue</span>
          </button>
        </div>
      } @else if(currentStep() === 5) {
        <div class="mt-10 pt-8 border-t border-[var(--cordic-black-10)]">
           <p class="text-sm text-[var(--cordic-black-80)] text-center mb-4">By clicking “Join SuperYes”, you confirm you’re authorised to act on <strong>{{ data().contactDetails.fleetName }}</strong>’s behalf.</p>
           <div class="flex items-center justify-between">
             <button (click)="saveAndGo('prev')" type="button" class="btn btn-secondary">Previous</button>
             <button (click)="submitApplication()" [disabled]="!isStep5Valid() || isSubmitting()" type="button" class="btn btn-success">
                @if (isSubmitting()) {
                   <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                   <span>Processing...</span>
                } @else {
                  @if (data().status === 'rejected') {
                    <span>Resubmit Application</span>
                  } @else {
                    <span>Join SuperYes</span>
                  }
                }
             </button>
           </div>
        </div>
      }
    </main>
  </div>
</div>

<!-- Reusable Document Uploader Template -->
<ng-template #documentUploader let-document="document" let-docKey="docKey" let-docTitle="docTitle">
  <h3 class="font-semibold text-lg text-[var(--cordic-black)] min-h-14">{{ docTitle }}</h3>
  @if (document?.url) {
    <div class="mt-4 space-y-4">
      <div class="flex items-start p-3 bg-white rounded-lg border">
        @if (document.fileName?.toLowerCase().endsWith('.pdf')) {
          <svg class="h-10 w-10 text-red-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a.375.375 0 01-.375-.375V6.75A3.75 3.75 0 009 3H5.625z M12.75 12a.75.75 0 000-1.5H9a.75.75 0 000 1.5h3.75z"/></svg>
        } @else {
          <img [src]="document.url" alt="Preview" class="w-10 h-10 rounded object-cover flex-shrink-0">
        }
        <div class="ml-3 min-w-0 flex-1">
          @if(isGuid(document.fileName)) {
            <p class="text-sm font-medium text-gray-900">File uploaded</p>
          } @else {
            <p class="text-sm font-medium text-gray-900 truncate">{{ document.fileName }}</p>
          }
          <div class="flex items-center gap-4 mt-1">
            <button (click)="viewDocument(document)" class="text-sm font-medium text-[var(--superyes-green)] hover:text-[var(--superyes-green-dark)]">View</button>
            <label [for]="docKey" class="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer">Replace
              <input [id]="docKey" type="file" class="sr-only" accept="image/*,application/pdf" (change)="handleFileInput($event, docKey)" capture="environment">
            </label>
          </div>
        </div>
      </div>
    </div>
  } @else {
    <div class="mt-4 relative">
      @if (isUploadingDoc() === docKey) {
        <div class="absolute inset-0 bg-white/80 flex flex-col items-center justify-center rounded-lg z-10 p-4">
          <svg class="animate-spin h-8 w-8 text-[var(--cordic-primary-100)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <p class="mt-2 text-sm font-medium text-gray-600">Uploading...</p>
        </div>
      }
      <label [for]="docKey" class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer">
        <svg class="mx-auto h-10 w-10 text-gray-300" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25-2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" /></svg>
        <span class="mt-2 block text-sm font-semibold text-[var(--superyes-green)]">Upload a file</span>
        <span class="mt-1 block text-xs text-gray-500">PNG, JPG, PDF up to 10MB</span>
        <input [id]="docKey" type="file" class="sr-only" accept="image/*,application/pdf" (change)="handleFileInput($event, docKey)" capture="environment">
      </label>
    </div>
  }
</ng-template>
